/**
 * Sièges où la carte diffère entre deux replays mais reste « équivalente »
 * (classe Type A/B, ou équivalence d'issue — dont bypass bots en max+).
 */

import type { CardNumber } from '../types/game';
import { countPoints } from './cards';
import { equivalenceClassKey } from './cardEquivalence';
import { handsAtStartOfTrick } from './dealHandsAtTrick';
import type {
  DiffereStatsDealDetail,
  DiffereStatsDealTrick,
  EquivMode,
} from './differeStatsReplay';

function trickCardsBySeat(tr: DiffereStatsDealTrick): Map<number, number> {
  const bySeat = new Map<number, number>();
  for (let i = 0; i < tr.cards.length; i++) {
    const seat = tr.players[i];
    if (seat == null || seat < 0) continue;
    bySeat.set(seat, tr.cards[i]!);
  }
  return bySeat;
}

function cardsByFixedSeat(tr: DiffereStatsDealTrick): (number | null)[] {
  const out: (number | null)[] = [null, null, null, null];
  for (let i = 0; i < tr.cards.length; i++) {
    const seat = tr.players[i];
    if (seat == null || seat < 0 || seat > 3) continue;
    out[seat] = tr.cards[i]!;
  }
  return out;
}

function fullTrickIdentical(
  a: DiffereStatsDealTrick | undefined,
  b: DiffereStatsDealTrick | undefined,
): boolean {
  if (!a || !b) return false;
  const sa = cardsByFixedSeat(a);
  const sb = cardsByFixedSeat(b);
  return sa.every((c, i) => c != null && c === sb[i]);
}

function otherSeatsIdentical(
  a: DiffereStatsDealTrick,
  b: DiffereStatsDealTrick,
  humanSeat: number,
): boolean {
  const sa = cardsByFixedSeat(a);
  const sb = cardsByFixedSeat(b);
  for (let seat = 0; seat < 4; seat++) {
    if (seat === humanSeat) continue;
    if (sa[seat] !== sb[seat]) return false;
  }
  return true;
}

function trickPoints(tr: DiffereStatsDealTrick): number | null {
  const bySeat = cardsByFixedSeat(tr);
  if (bySeat.some(c => c == null)) return null;
  return countPoints(bySeat as CardNumber[]);
}

function cardsSameEquivClassInHand(
  cardA: number,
  cardB: number,
  hand: number[] | undefined,
  equivOpts: { role: 'attacker' | 'defender'; enableTypeB: boolean; lowRankLimit: number },
): boolean {
  if (cardA === cardB || !hand?.length) return false;
  if (!hand.includes(cardA) || !hand.includes(cardB)) return false;
  const handIdx = hand.map(c => c - 1);
  return equivalenceClassKey(cardA - 1, handIdx, equivOpts)
    === equivalenceClassKey(cardB - 1, handIdx, equivOpts);
}

function humanPlayEquivalentAtTrick(
  baseline: DiffereStatsDealDetail,
  current: DiffereStatsDealDetail,
  trickIndex: number,
  equivOpts: { role: 'attacker' | 'defender'; enableTypeB: boolean; lowRankLimit: number },
): boolean {
  const trL = baseline.tricks.find(t => t.trickIndex === trickIndex);
  const trR = current.tricks.find(t => t.trickIndex === trickIndex);
  if (!trL || !trR) return false;
  const seat = baseline.humanSeat;
  const cardL = cardsByFixedSeat(trL)[seat];
  const cardR = cardsByFixedSeat(trR)[seat];
  if (cardL == null || cardR == null) return false;
  if (cardL === cardR) return true;
  const handsL = handsAtStartOfTrick(baseline, trickIndex + 1);
  const handsR = handsAtStartOfTrick(current, trickIndex + 1);
  return cardsSameEquivClassInHand(cardL, cardR, handsL?.[seat], equivOpts)
    || cardsSameEquivClassInHand(cardL, cardR, handsR?.[seat], equivOpts);
}

/** Miroir backend trickOutcomeEquivalentAtTrick (sans RankedHumanPlay). */
function trickOutcomeEquivalentAtTrick(
  baseline: DiffereStatsDealDetail,
  current: DiffereStatsDealDetail,
  trickIndex: number,
  equivOpts: { role: 'attacker' | 'defender'; enableTypeB: boolean; lowRankLimit: number },
  opts?: { maxPlus?: boolean; depth?: number },
): boolean {
  const byTrickL = new Map(baseline.tricks.map(t => [t.trickIndex, t]));
  const byTrickR = new Map(current.tricks.map(t => [t.trickIndex, t]));
  const trL = byTrickL.get(trickIndex);
  const trR = byTrickR.get(trickIndex);
  if (!trL || !trR) return false;
  if (fullTrickIdentical(trL, trR)) return true;

  const sameWinner = trL.winner === trR.winner;
  const humanSeat = baseline.humanSeat;
  const botsIdentical = otherSeatsIdentical(trL, trR, humanSeat);

  if (!botsIdentical) {
    const ptsL = trickPoints(trL);
    const ptsR = trickPoints(trR);
    const next = trickIndex + 1;
    const depth = opts?.depth ?? Math.max(baseline.tricks.length, current.tricks.length);
    const nextAligned = opts?.maxPlus === true
      && next < depth
      && (
        fullTrickIdentical(byTrickL.get(next), byTrickR.get(next))
        || trickOutcomeEquivalentAtTrick(baseline, current, next, equivOpts)
      );
    const canBypassBots = opts?.maxPlus === true
      && sameWinner
      && ptsL != null
      && ptsR === ptsL
      && nextAligned;
    if (!canBypassBots) return false;
  } else if (!sameWinner) {
    return false;
  }

  if (humanPlayEquivalentAtTrick(baseline, current, trickIndex, equivOpts)) return true;

  const cardL = cardsByFixedSeat(trL)[humanSeat];
  const cardR = cardsByFixedSeat(trR)[humanSeat];
  if (cardL == null || cardR == null) return false;
  return cardL !== cardR;
}

/** Clés `${trickIndex}:${seat}` — variante équivalente vs baseline. */
export function computeDealTrickSeatEquivVariantKeys(
  baseline: DiffereStatsDealDetail,
  current: DiffereStatsDealDetail,
  opts: {
    equivMode: EquivMode;
    lowRankLimit?: number;
    /** Si fourni, ne tester que ces sièges (ex. diffs déjà calculés). */
    candidateKeys?: ReadonlySet<string>;
  },
): Set<string> {
  const out = new Set<string>();
  if (opts.equivMode === 'off') return out;

  const equivOpts = {
    role: baseline.isAttackDeal ? 'attacker' as const : 'defender' as const,
    enableTypeB: true,
    lowRankLimit: opts.lowRankLimit ?? 5,
  };
  const outcomeOpts = opts.equivMode === 'maxplus'
    ? {
        maxPlus: true as const,
        depth: Math.max(baseline.tricks.length, current.tricks.length),
      }
    : undefined;

  const baselineByTrick = new Map(baseline.tricks.map(t => [t.trickIndex, t]));
  const candidates = opts.candidateKeys;

  for (const tr of current.tricks) {
    const prev = baselineByTrick.get(tr.trickIndex);
    if (!prev) continue;
    const prevBySeat = trickCardsBySeat(prev);
    const curBySeat = trickCardsBySeat(tr);
    const seats = new Set([...prevBySeat.keys(), ...curBySeat.keys()]);
    const handsL = handsAtStartOfTrick(baseline, tr.trickIndex + 1);
    const handsR = handsAtStartOfTrick(current, tr.trickIndex + 1);

    const outcomeEq = trickOutcomeEquivalentAtTrick(
      baseline,
      current,
      tr.trickIndex,
      equivOpts,
      outcomeOpts,
    );

    for (const seat of seats) {
      const key = `${tr.trickIndex}:${seat}`;
      if (candidates && !candidates.has(key)) continue;
      const cardL = prevBySeat.get(seat);
      const cardR = curBySeat.get(seat);
      if (cardL == null || cardR == null || cardL === cardR) continue;

      if (outcomeEq) {
        out.add(key);
        continue;
      }
      if (
        cardsSameEquivClassInHand(cardL, cardR, handsL?.[seat], equivOpts)
        || cardsSameEquivClassInHand(cardL, cardR, handsR?.[seat], equivOpts)
      ) {
        out.add(key);
      }
    }
  }
  return out;
}
